/**
 * Single vendor card on the Integrations page. Shows status, schedule,
 * last sync time. Read-only — action buttons (Configure / Test / Run now)
 * land in the follow-up commit.
 */
import React from "react"
import { CheckCircle2, AlertTriangle, Pause, Clock } from "lucide-react"

import { VendorCredential } from "../../api/integrations"
import { THEME } from "../../config/theme"
import { formatTimestamp } from "../../utils/format"

type Status = "active" | "failed" | "disabled" | "never_run"

const STATUS_PRESENTATION: Record<
  Status,
  { label: string; color: string; Icon: React.ComponentType<{ size?: number; strokeWidth?: number; style?: React.CSSProperties }> }
> = {
  active: {
    label: "Active",
    color: THEME.colors.semantic.success,
    Icon: CheckCircle2,
  },
  failed: {
    label: "Last run failed",
    color: THEME.colors.semantic.danger,
    Icon: AlertTriangle,
  },
  disabled: {
    label: "Disabled",
    color: THEME.colors.neutral.light.textSecondary,
    Icon: Pause,
  },
  never_run: {
    label: "Never run",
    color: THEME.colors.semantic.warning,
    Icon: Clock,
  },
}

function deriveStatus(c: VendorCredential): Status {
  if (!c.is_enabled) return "disabled"
  if (c.last_error_message && (!c.last_success_at || (c.last_run_at && c.last_run_at > c.last_success_at))) {
    return "failed"
  }
  if (!c.last_run_at) return "never_run"
  return "active"
}

const formatVendorName = (name: string): string =>
  name
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

interface Props {
  credential: VendorCredential
  canManage: boolean
  canTrigger: boolean
  onConfigure: () => void
  onTest: () => void
  onRunNow: () => void
  onDelete: () => void
  testResult?: { success: boolean; message: string } | null
  busyAction?: "test" | "run" | "delete" | null
}

const VendorCard: React.FC<Props> = ({
  credential,
  canManage,
  canTrigger,
  onConfigure,
  onTest,
  onRunNow,
  onDelete,
  testResult,
  busyAction,
}) => {
  const status = deriveStatus(credential)
  const presentation = STATUS_PRESENTATION[status]
  const StatusIcon = presentation.Icon

  const showActions = canManage || canTrigger

  return (
    <div
      style={{
        background: THEME.colors.neutral.light.surface,
        border: `1px solid ${THEME.colors.silver}`,
        borderLeft: `6px solid ${presentation.color}`,
        borderRadius: THEME.borderRadius.lg,
        padding: "16px 18px",
        fontFamily: THEME.fonts.primary,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {/* Vendor name + status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div
          style={{
            fontSize: "16px",
            fontWeight: 500,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          {formatVendorName(credential.vendor_name)}
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "12px",
            fontWeight: 500,
            color: presentation.color,
          }}
        >
          <StatusIcon size={14} strokeWidth={2} />
          {presentation.label}
        </div>
      </div>

      {/* Transport + schedule line */}
      <div
        style={{
          fontSize: "12px",
          color: THEME.colors.neutral.light.textSecondary,
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <span>{credential.transport_type.toUpperCase()}</span>
        <span aria-hidden>•</span>
        <span>{credential.cron_expression || "manual only"}</span>
      </div>

      {/* Last run details */}
      <div
        style={{
          marginTop: "4px",
          fontSize: "12px",
          color: THEME.colors.neutral.light.textSecondary,
          lineHeight: 1.5,
        }}
      >
        <div>Last sync: {credential.last_run_at ? formatTimestamp(credential.last_run_at) : "—"}</div>
        {credential.last_success_at && (
          <div>Last success: {formatTimestamp(credential.last_success_at)}</div>
        )}
        {status === "failed" && credential.last_error_message && (
          <div
            style={{
              marginTop: "6px",
              padding: "6px 8px",
              background: "rgba(220, 38, 38, 0.06)",
              borderRadius: THEME.borderRadius.md,
              color: THEME.colors.semantic.danger,
              fontSize: "11px",
              wordBreak: "break-word",
            }}
          >
            {credential.last_error_message}
          </div>
        )}
      </div>

      {/* Test result inline banner — shown only after a test action */}
      {testResult && (
        <div
          style={{
            marginTop: "4px",
            padding: "6px 8px",
            background: testResult.success
              ? "rgba(5, 150, 105, 0.08)"
              : "rgba(220, 38, 38, 0.08)",
            color: testResult.success
              ? THEME.colors.semantic.success
              : THEME.colors.semantic.danger,
            border: `0.5px solid ${
              testResult.success
                ? "rgba(5, 150, 105, 0.3)"
                : "rgba(220, 38, 38, 0.3)"
            }`,
            borderRadius: THEME.borderRadius.md,
            fontSize: "11px",
            wordBreak: "break-word",
          }}
        >
          {testResult.message}
        </div>
      )}

      {/* Action buttons */}
      {showActions && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: `1px solid ${THEME.colors.silver}`,
            fontSize: "12px",
          }}
        >
          {canManage && (
            <button
              type="button"
              onClick={onConfigure}
              style={linkBtnStyle(busyAction !== null && busyAction !== undefined)}
              disabled={busyAction !== null && busyAction !== undefined}
            >
              Configure
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={onTest}
              style={linkBtnStyle(busyAction !== null && busyAction !== undefined)}
              disabled={busyAction !== null && busyAction !== undefined}
            >
              {busyAction === "test" ? "Testing..." : "Test"}
            </button>
          )}
          {canTrigger && (
            <button
              type="button"
              onClick={onRunNow}
              disabled={!credential.is_enabled || (busyAction !== null && busyAction !== undefined)}
              style={linkBtnStyle(
                !credential.is_enabled || (busyAction !== null && busyAction !== undefined),
              )}
              title={credential.is_enabled ? undefined : "Credential is disabled"}
            >
              {busyAction === "run" ? "Triggering..." : "Run now"}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={onDelete}
              style={{
                ...linkBtnStyle(busyAction !== null && busyAction !== undefined),
                color: THEME.colors.semantic.danger,
                marginLeft: "auto",
              }}
              disabled={busyAction !== null && busyAction !== undefined}
            >
              {busyAction === "delete" ? "Deleting..." : "Delete"}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const linkBtnStyle = (disabled: boolean): React.CSSProperties => ({
  background: "none",
  border: "none",
  padding: 0,
  fontSize: "12px",
  fontFamily: THEME.fonts.primary,
  color: THEME.colors.navy,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
})

export default VendorCard
